import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import {
  formatHours,
  formatOperationalDays,
  formatFullAddress,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  AnimatedNumber,
  MotionList,
  MotionItem,
} from './themeHelpers';

const PLAYFUL_YELLOW = '#FFE14D';
const PLAYFUL_CORAL = '#FF6B6B';
const PLAYFUL_MINT = '#4ECDC4';
const PLAYFUL_DARK = '#1A1A1A';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

// Hard shadow style — the neo-memphis signature
function hardShadow(color = '#000', offset = 6) {
  return `${offset}px ${offset}px 0 ${color}`;
}

// Bouncy spring button wrapper
function BounceButton({ children, onClick, sx = {}, ...props }) {
  const shouldReduce = useReducedMotion();
  return (
    <Box
      component={shouldReduce ? 'span' : motion.span}
      whileHover={shouldReduce ? {} : { scale: 1.04 }}
      whileTap={shouldReduce ? {} : { scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
      style={{ display: 'inline-block' }}
    >
      <Button onClick={onClick} sx={sx} {...props}>
        {children}
      </Button>
    </Box>
  );
}

// Sticker badge
function Sticker({ children, bgcolor = '#000', color = '#fff', rotate = -2 }) {
  return (
    <Box
      sx={{
        display: 'inline-block',
        bgcolor,
        color,
        px: 1.5, py: 0.5,
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: 0.5,
        transform: `rotate(${rotate}deg)`,
        boxShadow: hardShadow('#000', 3),
        userSelect: 'none',
      }}
    >
      {children}
    </Box>
  );
}

// Bouncy plan card
function BounceCard({ children, accentColor, sx = {} }) {
  const shouldReduce = useReducedMotion();
  return (
    <Box
      component={shouldReduce ? 'div' : motion.div}
      whileHover={shouldReduce ? {} : { y: -6, rotate: 0.5 }}
      whileTap={shouldReduce ? {} : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 300, damping: 18 }}
      style={{ height: '100%' }}
    >
      <Box
        sx={{
          bgcolor: 'white',
          border: `3px solid ${PLAYFUL_DARK}`,
          borderRadius: 4,
          boxShadow: hardShadow('#000'),
          height: '100%',
          position: 'relative',
          ...sx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function PlayfulTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const galleryImages = images.slice(0, 6);

  const defaultDescription = `Hey! Welcome to ${library.name}! We're a fun, focused spot where students come to crush their goals. Great vibes, great seats, and zero distractions.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;

  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: PLAYFUL_YELLOW, color: PLAYFUL_DARK }}>

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: accentColor, border: `4px solid ${PLAYFUL_DARK}`, borderTop: 'none' }}>
        {/* Decorative circles */}
        <Box sx={{
          position: 'absolute', top: -60, right: -60, width: 200, height: 200,
          borderRadius: '50%', bgcolor: PLAYFUL_YELLOW, opacity: 0.35, pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -40, left: -40, width: 130, height: 130,
          borderRadius: '50%', bgcolor: PLAYFUL_CORAL, opacity: 0.4, pointerEvents: 'none',
        }} />

        <Box sx={{ position: 'relative', height: { xs: '50vh', md: '70vh' }, overflow: 'hidden' }}>
          <Box
            component="img"
            src={images[currentImageIndex]}
            alt={library.name}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.6))' }} />

          {/* Arrows */}
          {images.length > 1 && (
            <>
              <BounceButton
                onClick={prevImage}
                aria-label="Previous image"
                sx={{
                  position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 44, width: 44, height: 44, p: 0, borderRadius: '50%',
                  bgcolor: PLAYFUL_YELLOW, color: PLAYFUL_DARK,
                  border: `3px solid ${PLAYFUL_DARK}`, boxShadow: hardShadow('#000', 4),
                  '&:hover': { bgcolor: PLAYFUL_YELLOW },
                }}
              >
                <ArrowBack />
              </BounceButton>
              <BounceButton
                onClick={nextImage}
                aria-label="Next image"
                sx={{
                  position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                  minWidth: 44, width: 44, height: 44, p: 0, borderRadius: '50%',
                  bgcolor: PLAYFUL_YELLOW, color: PLAYFUL_DARK,
                  border: `3px solid ${PLAYFUL_DARK}`, boxShadow: hardShadow('#000', 4),
                  '&:hover': { bgcolor: PLAYFUL_YELLOW },
                }}
              >
                <ArrowForward />
              </BounceButton>
            </>
          )}

          {/* Dots */}
          {images.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
              {images.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  sx={{
                    width: 12, height: 12, borderRadius: '50%',
                    border: `2px solid ${PLAYFUL_DARK}`,
                    bgcolor: idx === currentImageIndex ? PLAYFUL_YELLOW : 'white',
                    cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: hardShadow('#000', 2),
                  }}
                />
              ))}
            </Box>
          )}

          {/* Hero text */}
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 5 } }}>
            <Container maxWidth="lg">
              <Reveal y={20}>
                <Box sx={{ display: 'inline-block', mb: 2 }}>
                  <Sticker bgcolor={PLAYFUL_CORAL} color="white" rotate={-2}>Study Space</Sticker>
                </Box>
                <Typography
                  variant="h2"
                  fontWeight={900}
                  sx={{
                    color: 'white',
                    fontSize: { xs: '2.2rem', md: '4.5rem' },
                    lineHeight: 1.05,
                    textShadow: `4px 4px 0 ${PLAYFUL_DARK}`,
                    mb: 2,
                  }}
                >
                  {library.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LocationOn sx={{ color: PLAYFUL_YELLOW }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>{library.address}</Typography>
                </Box>
                <BounceButton
                  onClick={() => onBookNow()}
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: PLAYFUL_YELLOW, color: PLAYFUL_DARK,
                    border: `3px solid ${PLAYFUL_DARK}`, boxShadow: hardShadow('#000'),
                    fontWeight: 900, fontSize: '1rem', px: 4, py: 1.25, borderRadius: 3,
                    '&:hover': { bgcolor: PLAYFUL_YELLOW },
                  }}
                >
                  Book a Seat!
                </BounceButton>
              </Reveal>
            </Container>
          </Box>
        </Box>
      </Box>

      {/* ── About + Stats ── */}
      <Box sx={{ bgcolor: PLAYFUL_YELLOW, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Reveal>
                <Typography variant="h4" fontWeight={900} sx={{ mb: 2, color: PLAYFUL_DARK }}>
                  Who are we?
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.85, color: '#333', fontSize: '1.05rem' }}>
                  {library.description || defaultDescription}
                </Typography>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={5}>
              <Reveal delay={0.1}>
                <Box
                  sx={{
                    bgcolor: 'white',
                    border: `3px solid ${PLAYFUL_DARK}`,
                    borderRadius: 4,
                    boxShadow: hardShadow('#000'),
                    p: 3,
                  }}
                >
                  {seatDisplay && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Typography variant="h2" fontWeight={900} sx={{ color: accentColor, lineHeight: 1 }}>
                        <AnimatedNumber value={seatDisplay} />
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                        seats available
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <AccessTime sx={{ color: accentColor, fontSize: 20, mt: 0.2 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {hoursStr || 'Contact for hours'}
                    </Typography>
                  </Box>
                  {daysStr && (
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                      <CalendarToday sx={{ color: accentColor, fontSize: 20, mt: 0.2 }} />
                      <Typography variant="body2">{daysStr}</Typography>
                    </Box>
                  )}
                  {library.locker_count > 0 && (
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <LockOutlined sx={{ color: accentColor, fontSize: 20, mt: 0.2 }} />
                      <Typography variant="body2">{library.locker_count} lockers</Typography>
                    </Box>
                  )}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Plans ── */}
      <Box sx={{ bgcolor: PLAYFUL_MINT, py: { xs: 6, md: 10 }, borderTop: `4px solid ${PLAYFUL_DARK}`, borderBottom: `4px solid ${PLAYFUL_DARK}` }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" fontWeight={900} sx={{ mb: 1, color: PLAYFUL_DARK }}>
              Pick your plan
            </Typography>
            <Typography variant="body2" sx={{ color: '#333', mb: 4 }}>No hidden fees. No surprises. Just study.</Typography>
          </Reveal>

          {plans.length > 0 ? (
            <Grid container spacing={3}>
              {plans.map((plan, idx) => {
                const planName = plan.name || titleCasePlanType(plan.plan_type);
                const price = formatPrice(plan.price_cents);
                const period = planPeriodLabel(plan.plan_type);
                const discPct = plan.first_time_discount;
                const discPrice = discountedFirstPrice(plan.price_cents, discPct);
                const blockColors = [accentColor, PLAYFUL_CORAL, PLAYFUL_YELLOW, PLAYFUL_MINT];
                const headerBg = blockColors[idx % blockColors.length];

                return (
                  <Grid item xs={12} sm={6} md={3} key={plan.id}>
                    <BounceCard accentColor={accentColor}>
                      {/* Colored header block */}
                      <Box sx={{ bgcolor: headerBg, p: 2, borderRadius: '12px 12px 0 0', borderBottom: `3px solid ${PLAYFUL_DARK}`, position: 'relative' }}>
                        {discPct > 0 && (
                          <Box sx={{ position: 'absolute', top: -10, right: 12 }}>
                            <Sticker bgcolor={PLAYFUL_CORAL} color="white" rotate={3}>
                              {discPct}% OFF!
                            </Sticker>
                          </Box>
                        )}
                        <Typography variant="subtitle1" fontWeight={900} sx={{ color: PLAYFUL_DARK }}>
                          {planName}
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        {price && (
                          <>
                            {discPct > 0 && discPrice !== null ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#aaa' }}>{price}</Typography>
                                <Typography variant="h4" fontWeight={900} sx={{ color: accentColor }}>{formatPrice(discPrice)}</Typography>
                                <Typography variant="caption" color="text.secondary">first time, then {price}</Typography>
                              </Box>
                            ) : (
                              <Typography variant="h3" fontWeight={900} sx={{ color: PLAYFUL_DARK, mb: 0.5 }}>{price}</Typography>
                            )}
                            {period && (
                              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>{period}</Typography>
                            )}
                          </>
                        )}

                        <Box sx={{ mb: 2 }}>
                          {plan.duration_days && (
                            <PlanFeature icon={<CalendarToday />} color="#555">
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature icon={<Schedule />} color="#555">
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature icon={<Repeat />} color={accentColor}>
                              Auto-renews
                            </PlanFeature>
                          )}
                        </Box>

                        {plan.description ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 2 }}
                          >
                            {plan.description}
                          </Typography>
                        ) : null}

                        <BounceButton
                          onClick={() => onBookNow(plan)}
                          fullWidth
                          variant="contained"
                          sx={{
                            bgcolor: PLAYFUL_DARK, color: 'white',
                            border: `2px solid ${PLAYFUL_DARK}`,
                            boxShadow: hardShadow(accentColor, 4),
                            borderRadius: 2, fontWeight: 700,
                            '&:hover': { bgcolor: PLAYFUL_DARK },
                          }}
                        >
                          Choose This
                        </BounceButton>
                      </Box>
                    </BounceCard>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <BounceCard accentColor={accentColor} sx={{ maxWidth: 480, mx: 'auto', p: 4, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Contact us for plans!</Typography>
              <BounceButton
                onClick={() => onBookNow()}
                variant="contained"
                sx={{
                  bgcolor: accentColor, color: 'white', fontWeight: 700,
                  border: `2px solid ${PLAYFUL_DARK}`, boxShadow: hardShadow('#000', 4),
                  '&:hover': { bgcolor: accentColor },
                }}
              >
                Get in Touch
              </BounceButton>
            </BounceCard>
          )}
        </Container>
      </Box>

      {/* ── Gallery ── */}
      <Box sx={{ bgcolor: PLAYFUL_YELLOW, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" fontWeight={900} sx={{ mb: 4, color: PLAYFUL_DARK }}>
              Take a peek inside!
            </Typography>
          </Reveal>
          <Grid container spacing={2}>
            {galleryImages.map((img, idx) => (
              <Grid item xs={6} md={4} key={idx}>
                <Box
                  component={motion.div}
                  whileHover={{ rotate: idx % 2 === 0 ? 1 : -1, scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <Box
                    component="img"
                    src={img}
                    alt={`Gallery ${idx + 1}`}
                    onClick={() => openLightbox(idx)}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: '100%', height: 200, objectFit: 'cover',
                      borderRadius: 3, cursor: 'pointer',
                      border: `3px solid ${PLAYFUL_DARK}`,
                      boxShadow: hardShadow('#000', 5),
                      display: 'block',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Amenities ── */}
      {library.amenities?.length > 0 && (
        <Box sx={{ bgcolor: PLAYFUL_CORAL, borderTop: `4px solid ${PLAYFUL_DARK}`, borderBottom: `4px solid ${PLAYFUL_DARK}`, py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <Typography variant="h4" fontWeight={900} sx={{ mb: 4, color: 'white' }}>
                What's included
              </Typography>
            </Reveal>
            <MotionList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16 }}>
              {library.amenities.map((amenity, idx) => (
                <MotionItem key={idx}>
                  <Box
                    component={motion.div}
                    whileHover={{ scale: 1.06, rotate: -1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                  >
                    <Box
                      sx={{
                        bgcolor: PLAYFUL_YELLOW, textAlign: 'center', p: 2,
                        border: `3px solid ${PLAYFUL_DARK}`,
                        borderRadius: 3,
                        boxShadow: hardShadow('#000', 4),
                      }}
                    >
                      <Box sx={{ color: accentColor, mb: 1 }}>
                        {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 32 } })}
                      </Box>
                      <Typography variant="caption" fontWeight={700} sx={{ color: PLAYFUL_DARK }}>
                        {amenity}
                      </Typography>
                    </Box>
                  </Box>
                </MotionItem>
              ))}
            </MotionList>
          </Container>
        </Box>
      )}

      {/* ── Location ── */}
      <Box sx={{ bgcolor: PLAYFUL_YELLOW, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" fontWeight={900} sx={{ mb: 4, color: PLAYFUL_DARK }}>Find us!</Typography>
          </Reveal>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 300, border: `3px solid ${PLAYFUL_DARK}`,
                  borderRadius: 3, boxShadow: hardShadow('#000'), overflow: 'hidden',
                }}
              >
                {library.lat && library.lng ? (
                  <iframe
                    title="Library Location"
                    width="100%" height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                    <Typography color="text.secondary">Map unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: 'white', p: 3, border: `3px solid ${PLAYFUL_DARK}`,
                  borderRadius: 3, boxShadow: hardShadow('#000'),
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}
              >
                {fullAddress && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LocationOn sx={{ color: accentColor, flexShrink: 0, mt: 0.3 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>Address</Typography>
                      <Typography variant="body2" color="text.secondary">{fullAddress}</Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Phone sx={{ color: accentColor, flexShrink: 0, mt: 0.3 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>Phone</Typography>
                      <Typography variant="body2" color="text.secondary">
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Email sx={{ color: accentColor, flexShrink: 0, mt: 0.3 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>Email</Typography>
                      <Typography variant="body2" color="text.secondary">
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ bgcolor: accentColor, borderTop: `4px solid ${PLAYFUL_DARK}`, py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Reveal>
            <Typography variant="h3" fontWeight={900} sx={{ color: 'white', textShadow: `3px 3px 0 ${PLAYFUL_DARK}`, mb: 2 }}>
              Let's go! Book today.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontWeight: 600 }}>
              Grab your spot at {library.name} before it fills up!
            </Typography>
            <BounceButton
              onClick={() => onBookNow()}
              variant="contained"
              size="large"
              sx={{
                bgcolor: PLAYFUL_YELLOW, color: PLAYFUL_DARK,
                border: `3px solid ${PLAYFUL_DARK}`, boxShadow: hardShadow('#000'),
                fontWeight: 900, fontSize: '1.1rem', px: 5, py: 1.5,
                borderRadius: 4,
                '&:hover': { bgcolor: PLAYFUL_YELLOW },
              }}
            >
              Book Now!
            </BounceButton>
          </Reveal>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: PLAYFUL_DARK, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{library.name}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: PLAYFUL_DARK, border: `4px solid ${PLAYFUL_YELLOW}` } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          aria-label="Close gallery"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={galleryImages[lightboxIdx]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default PlayfulTheme;
